import type { ReactNode } from "react";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { cn } from "@/lib/utils";
import { NotificacionesAdmin } from "./NotificacionesAdmin";
import { BotonMiCuenta } from "./BotonMiCuenta";

/**
 * Acciones del usuario en el encabezado de cualquier portal del admin, iguales a las de
 * `AdminHeader`: [Ver como] + campana de avisos + nombre y rol + avatar con foto que abre
 * "Mi cuenta". Cada portal conserva su layout (logo, título, menú) y su selector "Ver como",
 * que entra por `verComo` (su lógica no cambia; el ancho lo pone `SelectorVerComo`).
 *
 * - `compacto` (encabezado móvil): sin nombre ni rol y sin "Ver como"; este va en su propia fila
 *   a todo el ancho con `FilaVerComoMovil`. La campana y el avatar siempre se ven.
 * - Nombre, rol y avatar son los de la identidad efectiva (la persona vista con "Ver como"); su
 *   cuenta se abre en solo lectura (`BotonMiCuenta`).
 */
export function AccionesUsuarioCabecera({
  verComo,
  compacto = false,
  className,
}: {
  /** Selector "Ver como" propio del portal (ya validado por permiso dentro del selector). */
  verComo?: ReactNode;
  compacto?: boolean;
  className?: string;
}) {
  // Identidad efectiva: la de la persona vista con "Ver como"; si no, la de la sesión.
  const perfil = usePerfilVista();
  const nombre = perfil.nombre.split(/\s+/).slice(0, 2).join(" ");
  return (
    <div className={cn("flex min-w-0 shrink-0 items-center gap-2.5", className)}>
      {!compacto && verComo}
      <NotificacionesAdmin />
      {!compacto && (
        <div className="hidden min-w-0 text-right xl:block">
          <p className="truncate text-sm font-medium leading-tight text-foreground">{nombre}</p>
          <p className="truncate text-xs leading-tight text-muted-foreground">{perfil.rol}</p>
        </div>
      )}
      <BotonMiCuenta />
    </div>
  );
}

/** "Ver como" en móvil: segunda fila a todo el ancho, igual que en `AdminHeader`. */
export function FilaVerComoMovil({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <div className="px-3 pb-3 lg:hidden">{children}</div>;
}
